/**
 * Parses errors from FastAPI backends into a human-readable string.
 *
 * FastAPI surfaces errors in two shapes, and the payment API in a third:
 *
 *   Validation error (422):
 *   { "detail": [{ "msg": "Field required", "loc": ["body", "chat_id"], ... }] }
 *
 *   Simple error (4xx / 5xx):
 *   { "detail": "Chat not found" }
 *
 *   Payment API (e.g. /payment/create-checkout-session):
 *   { "message": "This plan is not available in your region" }
 *
 * Falls back gracefully for non-FastAPI errors (plain strings, Error objects,
 * Axios errors, unknown shapes).
 */

type FastApiValidationItem = {
	msg: string;
	loc?: (string | number)[];
	type?: string;
};

type FastApiErrorBody =
	| {
			detail?: string | FastApiValidationItem[];
			/** The payment API returns `{ message }` rather than FastAPI's `detail`. */
			message?: string;
	  }
	| FastApiValidationItem[];

const FALLBACK_MESSAGE = 'Something went wrong. Please try again.';

const MAX_PRESENTABLE_LENGTH = 300;

// Gateway/proxy failures (502/504) return an HTML error page, not a FastAPI body — never surface that raw.
function isPresentable(text: string): boolean {
	const trimmed = text.trim();
	if (!trimmed || trimmed.length > MAX_PRESENTABLE_LENGTH) return false;
	return !trimmed.startsWith('<') && !/<\/?(html|head|body|center|h1)\b/i.test(trimmed);
}

function extractFromFastApiBody(body: FastApiErrorBody): string | null {
	if (!Array.isArray(body) && typeof body.message === 'string' && isPresentable(body.message)) {
		return body.message;
	}

	const detail = Array.isArray(body) ? body : body.detail;

	if (typeof detail === 'string') return detail;

	if (Array.isArray(detail) && detail.length > 0) {
		const messages = detail
			.map((item) => {
				if (!item?.msg) return '';
				const location = item.loc?.slice(1).join(' - ') ?? '';
				return location ? `${location}: ${item.msg}` : item.msg;
			})
			.filter(Boolean);
		return messages.length ? messages.join('; ') : null;
	}

	return null;
}

function tryParseJson(text: string): FastApiErrorBody | null {
	try {
		return JSON.parse(text) as FastApiErrorBody;
	} catch {
		return null;
	}
}

/**
 * Returns a human-readable error message from any error shape.
 * Safe to call with `unknown` — never throws.
 */
export function parseFastApiError(error: unknown): string {
	if (!error) return FALLBACK_MESSAGE;

	// Axios error
	if (
		typeof error === 'object' &&
		error !== null &&
		'response' in error &&
		typeof (error as { response?: unknown }).response === 'object'
	) {
		const axiosError = error as { response: { data?: unknown } };
		const data = axiosError.response.data;

		if (typeof data === 'object' && data !== null) {
			const parsed = extractFromFastApiBody(data as FastApiErrorBody);
			if (parsed) return parsed;
		}

		if (typeof data === 'string') {
			const json = tryParseJson(data);
			if (json) {
				const parsed = extractFromFastApiBody(json);
				if (parsed) return parsed;
			}
			if (isPresentable(data)) return data;
		}
	}

	// Native Error / SDK streaming error — message may be a serialised JSON body
	if (error instanceof Error) {
		const json = tryParseJson(error.message);
		if (json) {
			const parsed = extractFromFastApiBody(json);
			if (parsed) return parsed;
		}
		if (isPresentable(error.message)) return error.message;
	}

	// Plain FastAPI body passed directly
	if (typeof error === 'object' && error !== null && 'detail' in error) {
		const parsed = extractFromFastApiBody(error as FastApiErrorBody);
		if (parsed) return parsed;
	}

	return FALLBACK_MESSAGE;
}
